import React, { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, Check, Loader2, RefreshCw, User, Shirt, Footprints } from "lucide-react";

const SLOTS = [
  { key: "face", label: "Face", hint: "Clear photo of your face (clean-shaven)", Icon: User },
  { key: "uniform", label: "Full uniform", hint: "Head-to-knee shot of your uniform", Icon: Shirt },
  { key: "shoes", label: "Shoes", hint: "Photo of the shoes you're wearing", Icon: Footprints },
];

// Read a File, downscale to <=1024px and return a JPEG data URL (keeps payload small).
const fileToScaledDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = reject;
  reader.onload = () => {
    const img = new Image();
    img.onerror = reject;
    img.onload = () => {
      const max = 1024;
      let { width, height } = img;
      if (width > max || height > max) {
        const r = Math.min(max / width, max / height);
        width = Math.round(width * r); height = Math.round(height * r);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

/**
 * Grooming photo capture for shift-start (clock-in / end-break). Collects 3
 * photos (face, uniform, shoes). On phones the file input opens the camera
 * directly; on desktop it opens the file picker.
 */
export default function GroomingCaptureModal({ open, eventLabel = "Clock in", busy = false, onCancel, onSubmit }) {
  const [photos, setPhotos] = useState({ face: null, uniform: null, shoes: null });
  const [loadingSlot, setLoadingSlot] = useState(null);
  const inputs = { face: useRef(null), uniform: useRef(null), shoes: useRef(null) };

  const pick = async (key, file) => {
    if (!file) return;
    setLoadingSlot(key);
    try {
      const dataUrl = await fileToScaledDataUrl(file);
      setPhotos((p) => ({ ...p, [key]: dataUrl }));
    } finally {
      setLoadingSlot(null);
    }
  };

  const allDone = photos.face && photos.uniform && photos.shoes;

  const reset = () => setPhotos({ face: null, uniform: null, shoes: null });

  const submit = () => {
    if (!allDone) return;
    onSubmit({ face_b64: photos.face, uniform_b64: photos.uniform, shoes_b64: photos.shoes });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) { reset(); onCancel?.(); } }}>
      <DialogContent className="max-w-lg" data-testid="grooming-capture-modal">
        <DialogHeader>
          <DialogTitle>Grooming check — {eventLabel}</DialogTitle>
          <DialogDescription>
            Submit 3 photos so we can verify grooming standards. All three are required.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3">
          {SLOTS.map(({ key, label, hint, Icon }) => (
            <div key={key} className="flex flex-col">
              <input
                ref={inputs[key]}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                data-testid={`grooming-input-${key}`}
                onChange={(e) => pick(key, e.target.files?.[0])}
              />
              <button
                type="button"
                onClick={() => inputs[key].current?.click()}
                disabled={busy}
                data-testid={`grooming-slot-${key}`}
                className={`relative aspect-square rounded-lg border-2 overflow-hidden flex flex-col items-center justify-center text-center transition-colors ${photos[key] ? "border-emerald-400" : "border-dashed border-zinc-300 hover:border-zinc-400 bg-zinc-50"}`}
              >
                {loadingSlot === key ? (
                  <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
                ) : photos[key] ? (
                  <>
                    <img src={photos[key]} alt={label} className="absolute inset-0 w-full h-full object-cover" />
                    <span className="absolute top-1 right-1 bg-emerald-500 text-white rounded-full p-0.5"><Check className="w-3 h-3" /></span>
                  </>
                ) : (
                  <>
                    <Icon className="w-6 h-6 text-zinc-400" />
                    <Camera className="w-3.5 h-3.5 text-zinc-400 mt-1" />
                  </>
                )}
              </button>
              <div className="mt-1.5 text-center">
                <div className="text-xs font-semibold">{label}</div>
                <div className="text-[10px] text-zinc-400 leading-tight">{hint}</div>
                {photos[key] && (
                  <button type="button" onClick={() => inputs[key].current?.click()} disabled={busy} className="text-[10px] text-zinc-500 hover:text-zinc-800 inline-flex items-center gap-0.5 mt-0.5" data-testid={`grooming-retake-${key}`}>
                    <RefreshCw className="w-2.5 h-2.5" /> Retake
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onCancel?.(); }} disabled={busy} data-testid="grooming-cancel">Cancel</Button>
          <Button onClick={submit} disabled={!allDone || busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="grooming-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}
            {eventLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
